import { useEffect, useState } from "react";

import { PaginationItem } from "./pagination-item.component";
import styles from "./pagination.module.css";

function generatePagesArray(
  from: number,
  to: number,
  total: number,
  maxPages = 5
) {
  if (total <= 0 || from > to) {
    return [];
  }

  const length = Math.min(to - from + 1, total);
  const maxVisiblePages = Math.min(length, maxPages);

  const pagesArray = Array.from(
    { length: maxVisiblePages },
    (_, index) => from + index
  ).filter((page) => page > 0 && page);

  return pagesArray;
}
export interface PaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  total: number;
  limit?: number;
  currentPage?: number;
  token?: "primary" | "secondary";
  onPageChange: (page: number) => void;
  variant?: "simple" | "compound" | "first-page";
  dataQA?: string;
}

export const Pagination = ({
  total,
  limit = 10,
  currentPage = 1,
  onPageChange,
  variant = "compound",
  dataQA = "pagination-ds",
  ...props
}: PaginationProps) => {
  const [page, setPage] = useState(1);
  const displayFirstPage = currentPage! > 5;

  const pagesCount = Math.ceil(total / limit);

  const startOffset = variant === "simple" ? 0 : -1;
  const endOffset = variant === "simple" ? -1 : -2;

  const count = displayFirstPage ? 5 : 6;

  const siblingsCount = variant === "simple" ? count : 4;
  const leftSiblingsCount =
    currentPage <= 5 ? 4 : Math.floor(siblingsCount / 2) + startOffset;
  const rightSiblingsCount =
    currentPage <= 5 ? 1 : siblingsCount - leftSiblingsCount + endOffset;

  let leftBound = page - leftSiblingsCount;
  let rightBound = page + rightSiblingsCount;

  if (leftBound <= 1) {
    leftBound = 1;
    rightBound = siblingsCount;
  }

  if (rightBound >= pagesCount) {
    rightBound = pagesCount;
    leftBound = pagesCount - siblingsCount + 1;
  }

  const previousPages = generatePagesArray(leftBound, page - 1, total);
  const nextPages = generatePagesArray(page + 1, rightBound, total);

  const isFirstPage = page === 1;
  const isLastPage = page === pagesCount;

  useEffect(() => {
    setPage((prevPage) => {
      if (currentPage !== prevPage) {
        return currentPage;
      }

      if (!nextPages.length && pagesCount !== prevPage) {
        onPageChange(pagesCount);
        return pagesCount;
      }

      return prevPage;
    });
  }, [currentPage, limit, nextPages, onPageChange, total]);

  const goToPreviousPage = () => {
    if (!isFirstPage) {
      onPageChange(Math.max(page - 1, 1));
    }
  };

  const goToNextPage = () => {
    if (!isLastPage) {
      onPageChange(Math.min(page + 1, pagesCount));
    }
  };

  if (limit === 0) {
    return null;
  }

  return (
    <div
      className={`${styles.paginationWrapper} ${
        variant === "compound" ? styles.compound : ""
      }`}
      data-testid="pagination-component"
      data-qa={dataQA}
      {...props}
      data-ds="ds-pagination"
    >
      <button
        className={styles.arrow}
        disabled={isFirstPage}
        onClick={goToPreviousPage}
        data-testid="previous-page"
        aria-label="Página anterior"
        type="button"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.0777 14.756L6.911 10.5894C6.58517 10.2635 6.58517 9.73688 6.911 9.41104L11.0777 5.24437C11.4035 4.91854 11.9302 4.91854 12.256 5.24437C12.5818 5.57021 12.5818 6.09687 12.256 6.42271L8.6785 10.0002L12.256 13.5777C12.5818 13.9035 12.5818 14.4302 12.256 14.756C12.0935 14.9185 11.8802 15.0002 11.6668 15.0002C11.4535 15.0002 11.2402 14.9185 11.0777 14.756Z"
            fill="#2E2D37"
          />
        </svg>
      </button>

      {displayFirstPage && (
        <>
          <PaginationItem onPageChange={onPageChange} number={1} />

          <span className={styles.Ellipsis}>...</span>
        </>
      )}

      <div className={styles.paginationNumbers}>
        {variant === "compound" && leftBound > 1 && (
          <>
            <PaginationItem onPageChange={onPageChange} number={1} />
            {leftBound > 2 && <span className={styles.ellipsis}>...</span>}
          </>
        )}

        {previousPages.length > 0 &&
          previousPages.map((page) => (
            <PaginationItem
              onPageChange={onPageChange}
              key={page}
              number={page}
            />
          ))}

        <PaginationItem onPageChange={onPageChange} number={page} isCurrent />

        {nextPages.length > 0 &&
          nextPages.map((page) => (
            <PaginationItem
              onPageChange={onPageChange}
              key={page}
              number={page}
            />
          ))}

        {variant === "compound" && rightBound < pagesCount && (
          <>
            {rightBound < pagesCount - 1 && (
              <span className={styles.ellipsis}>...</span>
            )}
            <PaginationItem onPageChange={onPageChange} number={pagesCount} />
          </>
        )}
      </div>

      <button
        className={styles.arrow}
        disabled={isLastPage}
        onClick={goToNextPage}
        data-testid="next-page"
        aria-label="Próxima página"
        type="button"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.9227 14.756L13.0894 10.5894C13.4152 10.2635 13.4152 9.73688 13.0894 9.41104L8.9227 5.24437C8.59686 4.91854 8.0702 4.91854 7.74436 5.24437C7.41853 5.57021 7.41853 6.09687 7.74436 6.42271L11.3219 10.0002L7.74436 13.5777C7.41853 13.9035 7.41853 14.4302 7.74436 14.756C7.90686 14.9185 8.1202 15.0002 8.33353 15.0002C8.54686 15.0002 8.7602 14.9185 8.9227 14.756Z"
            fill="#2E2D37"
          />
        </svg>
      </button>
    </div>
  );
};
